import type {
  ImportDatabase,
  ImportEntity,
  ImportProduct,
  ImportNotification,
  PgaAgency,
  FtaProgram,
  UflpaRisk,
  ImportRisk,
  ImportEntry,
  EntryStatus,
} from "./import-types";

const today = new Date();
const addDays = (d: number) => {
  const x = new Date(today);
  x.setDate(x.getDate() + d);
  return x.toISOString();
};

export const IMPORT_DATABASES: ImportDatabase[] = [
  {
    id: "idb-elec",
    name: "Electronics & Components — APAC",
    description: "Semiconductors, PCBAs, and finished consumer electronics imported from China, Vietnam, Taiwan, and Malaysia.",
    ownerId: "u1",
    createdAt: addDays(-200),
    icon: "Cpu",
    accent: "--action-color",
    visibility: "shared",
  },
  {
    id: "idb-apparel",
    name: "Apparel & Textiles",
    description: "Cotton, synthetic, and blended apparel with elevated UFLPA / forced-labor exposure across South & Southeast Asia.",
    ownerId: "u2",
    createdAt: addDays(-150),
    icon: "Shirt",
    accent: "--accent-color",
    visibility: "shared",
  },
  {
    id: "idb-machinery",
    name: "Industrial Machinery",
    description: "Heavy machinery, machine tools, and steel-intensive equipment subject to Section 232 duties.",
    ownerId: "u3",
    createdAt: addDays(-120),
    icon: "Wrench",
    accent: "--warning",
    visibility: "shared",
  },
  {
    id: "idb-chem",
    name: "Chemicals & Pharma",
    description: "Bulk chemicals, APIs, and finished pharmaceuticals with FDA / EPA / TSCA review requirements.",
    ownerId: "u3",
    createdAt: addDays(-90),
    icon: "FlaskConical",
    accent: "--success",
    visibility: "shared",
  },
  {
    id: "idb-auto",
    name: "Automotive Parts",
    description: "OEM and aftermarket automotive components imported under USMCA and KORUS programs.",
    ownerId: "u4",
    createdAt: addDays(-60),
    icon: "Car",
    accent: "--danger",
    visibility: "shared",
  },
];

export const IMPORT_ENTITIES: ImportEntity[] = [
  { id: "ie-1", name: "Shenzhen Apex Electronics", role: "supplier", country: "China", riskTier: "medium" },
  { id: "ie-2", name: "Hanoi Precision Mfg", role: "manufacturer", country: "Vietnam", riskTier: "low" },
  { id: "ie-3", name: "Taipei Silicon Works", role: "manufacturer", country: "Taiwan", riskTier: "low" },
  { id: "ie-4", name: "Penang Assembly Co", role: "supplier", country: "Malaysia", riskTier: "low" },
  { id: "ie-5", name: "Dhaka Garment Group", role: "supplier", country: "Bangladesh", riskTier: "medium" },
  { id: "ie-6", name: "Xinjiang Cotton Mills", role: "supplier", country: "China", riskTier: "high" },
  { id: "ie-7", name: "Tirupur Knitwear", role: "manufacturer", country: "India", riskTier: "low" },
  { id: "ie-8", name: "Stuttgart Werkzeug GmbH", role: "manufacturer", country: "Germany", riskTier: "low" },
  { id: "ie-9", name: "Osaka Tooling KK", role: "manufacturer", country: "Japan", riskTier: "low" },
  { id: "ie-10", name: "Mumbai Pharma Labs", role: "manufacturer", country: "India", riskTier: "medium" },
  { id: "ie-11", name: "Basel Chemie AG", role: "supplier", country: "Switzerland", riskTier: "low" },
  { id: "ie-12", name: "Guangzhou Chem Trading", role: "supplier", country: "China", riskTier: "medium" },
  { id: "ie-13", name: "Monterrey Autoparts SA", role: "manufacturer", country: "Mexico", riskTier: "low" },
  { id: "ie-14", name: "Seoul Drivetrain Ltd", role: "manufacturer", country: "South Korea", riskTier: "low" },
  { id: "ie-15", name: "Pacific Logistics LLC", role: "consignee", country: "United States", riskTier: "low" },
];

type Row = [
  id: string,
  sku: string,
  name: string,
  hts: string,
  origin: string,
  supplierId: string,
  mfgId: string,
  value: number,
  dbId: string,
  score: number,
  risk: ImportRisk,
  pga: PgaAgency[],
  s301: number, // rate, 0 means not subject
  s232: number,
  addDuty: number,
  fta: FtaProgram,
  ftaEligible: boolean,
  uflpa: UflpaRisk,
  _unused: number,
];

const ROWS: Row[] = [
  // Electronics — APAC (idb-elec)
  ["ip-001","ELE-1001","Bluetooth audio module","8517.62.00","China","ie-1","ie-1",120000,"idb-elec",78,"Medium",[],25,0,0,"none",false,"low",-3],
  ["ip-002","ELE-1002","Mid-tier smartphone PCBA","8473.30.11","Vietnam","ie-2","ie-2",450000,"idb-elec",88,"Low",[],0,0,0,"none",false,"none",-1],
  ["ip-003","ELE-1003","Power adapter 65W USB-C","8504.40.95","China","ie-1","ie-1",62000,"idb-elec",54,"High",["CPSC"],25,0,0,"none",false,"low",-9],
  ["ip-004","ELE-1004","Logic-grade silicon wafer","3818.00.00","Taiwan","ie-3","ie-3",980000,"idb-elec",94,"Low",[],0,0,0,"none",false,"none",-2],
  ["ip-005","ELE-1005","DDR5 memory module","8542.32.00","South Korea","ie-14","ie-14",330000,"idb-elec",90,"Low",[],0,0,0,"KORUS",true,"none",-4],
  ["ip-006","ELE-1006","HDMI cable assembly","8544.42.90","China","ie-1","ie-1",18000,"idb-elec",62,"Medium",["CPSC"],25,0,0,"none",false,"low",-12],
  ["ip-007","ELE-1007","Server NIC card","8517.62.00","Malaysia","ie-4","ie-4",210000,"idb-elec",86,"Low",[],0,0,0,"none",false,"none",-5],
  ["ip-008","ELE-1008","Lithium-ion battery pack","8507.60.00","China","ie-1","ie-1",240000,"idb-elec",45,"High",["DOT","CPSC"],25,0,0,"none",false,"medium",-7],

  // Apparel & Textiles (idb-apparel) — high UFLPA exposure
  ["ip-101","APP-2001","Men's cotton woven shirts","6205.20.20","Bangladesh","ie-5","ie-5",140000,"idb-apparel",72,"Medium",[],0,0,0,"none",false,"low",-6],
  ["ip-102","APP-2002","Women's cotton denim jeans","6204.62.40","China","ie-6","ie-6",260000,"idb-apparel",28,"Critical",[],25,0,0,"none",false,"high",-14],
  ["ip-103","APP-2003","Synthetic activewear leggings","6104.63.20","Vietnam","ie-2","ie-2",98000,"idb-apparel",84,"Low",[],0,0,0,"none",false,"none",-2],
  ["ip-104","APP-2004","Cotton knit T-shirts","6109.10.00","India","ie-7","ie-7",75000,"idb-apparel",80,"Low",[],0,0,0,"none",false,"none",-3],
  ["ip-105","APP-2005","Polyester fleece jackets","6101.30.20","China","ie-6","ie-6",185000,"idb-apparel",36,"High",[],25,0,0,"none",false,"high",-11],
  ["ip-106","APP-2006","Yarn-dyed cotton fabric","5208.41.00","China","ie-6","ie-6",92000,"idb-apparel",40,"High",[],25,0,0,"none",false,"high",-8],
  ["ip-107","APP-2007","Wool blend suiting","5112.19.60","India","ie-7","ie-7",55000,"idb-apparel",78,"Medium",[],0,0,0,"none",false,"low",-15],

  // Industrial Machinery (idb-machinery) — Section 232 heavy
  ["ip-201","MCH-3001","Hydraulic press 200T","8462.91.80","China","ie-1","ie-1",520000,"idb-machinery",58,"Medium",[],25,25,0,"none",false,"none",-20],
  ["ip-202","MCH-3002","CNC vertical mill","8459.61.00","Germany","ie-8","ie-8",780000,"idb-machinery",91,"Low",[],0,0,0,"none",false,"none",-4],
  ["ip-203","MCH-3003","Steel plate (hot-rolled)","7208.51.00","China","ie-1","ie-1",340000,"idb-machinery",42,"High",[],25,25,0,"none",false,"low",-9],
  ["ip-204","MCH-3004","Aluminum extrusions","7604.21.00","China","ie-1","ie-1",180000,"idb-machinery",50,"Medium",[],25,10,0,"none",false,"low",-6],
  ["ip-205","MCH-3005","Industrial robot arm","8479.50.00","Japan","ie-9","ie-9",640000,"idb-machinery",93,"Low",[],0,0,0,"none",false,"none",-3],
  ["ip-206","MCH-3006","Wind turbine gearbox","8483.40.50","Germany","ie-8","ie-8",1100000,"idb-machinery",88,"Low",[],0,0,0,"none",false,"none",-12],
  ["ip-207","MCH-3007","Carbon steel pipe fittings","7307.99.50","China","ie-1","ie-1",95000,"idb-machinery",46,"Medium",[],25,25,0,"none",false,"low",-18],

  // Chemicals & Pharma (idb-chem) — PGA heavy
  ["ip-301","CHM-4001","Active pharmaceutical ingredient (generic)","2941.90.10","India","ie-10","ie-10",420000,"idb-chem",82,"Medium",["FDA"],0,0,0,"none",false,"none",-5],
  ["ip-302","CHM-4002","Specialty solvent (industrial)","2902.30.00","Switzerland","ie-11","ie-11",260000,"idb-chem",86,"Low",["EPA","TSCA"],0,0,0,"none",false,"none",-2],
  ["ip-303","CHM-4003","Finished tablets (analgesic)","3004.90.92","India","ie-10","ie-10",310000,"idb-chem",80,"Medium",["FDA"],0,0,0,"none",false,"none",-7],
  ["ip-304","CHM-4004","Industrial pigment (organic)","3204.17.90","China","ie-12","ie-12",75000,"idb-chem",58,"Medium",["EPA","TSCA"],25,0,0,"none",false,"low",-10],
  ["ip-305","CHM-4005","Agricultural fertilizer blend","3105.20.00","China","ie-12","ie-12",140000,"idb-chem",60,"Medium",["EPA","USDA"],25,0,0,"none",false,"low",-13],
  ["ip-306","CHM-4006","Lab reagent kit","3822.00.50","Germany","ie-8","ie-8",48000,"idb-chem",90,"Low",["FDA"],0,0,0,"none",false,"none",-4],
  ["ip-307","CHM-4007","Crop protection (herbicide)","3808.93.20","China","ie-12","ie-12",96000,"idb-chem",44,"High",["EPA","USDA"],25,0,0,"none",false,"medium",-21],

  // Automotive Parts (idb-auto) — FTA-rich
  ["ip-401","AUT-5001","Brake caliper assembly","8708.30.50","Mexico","ie-13","ie-13",280000,"idb-auto",92,"Low",[],0,0,0,"USMCA",true,"none",-2],
  ["ip-402","AUT-5002","Drive shaft","8708.50.89","South Korea","ie-14","ie-14",195000,"idb-auto",89,"Low",[],0,0,0,"KORUS",true,"none",-3],
  ["ip-403","AUT-5003","Headlamp assembly","8512.20.40","Mexico","ie-13","ie-13",165000,"idb-auto",90,"Low",[],0,0,0,"USMCA",true,"none",-1],
  ["ip-404","AUT-5004","Cast aluminum wheels","8708.70.45","China","ie-1","ie-1",240000,"idb-auto",48,"High",["DOT"],25,10,0,"none",false,"low",-9],
  ["ip-405","AUT-5005","Catalytic converter","8708.92.50","Mexico","ie-13","ie-13",520000,"idb-auto",94,"Low",["EPA"],0,0,0,"USMCA",true,"none",-2],
  ["ip-406","AUT-5006","Tire — passenger radial","4011.10.10","South Korea","ie-14","ie-14",310000,"idb-auto",86,"Low",["DOT"],0,0,0,"KORUS",true,"none",-4],
  ["ip-407","AUT-5007","Stamped steel body panel","8708.29.50","China","ie-1","ie-1",175000,"idb-auto",52,"Medium",[],25,25,0,"none",false,"low",-15],
  ["ip-408","AUT-5008","Wiring harness","8544.30.00","Mexico","ie-13","ie-13",98000,"idb-auto",91,"Low",[],0,0,0,"USMCA",true,"none",-6],
  // Classification-health demo rows
  ["ip-009","ELE-1009","Unclassified IoT sensor","","Vietnam","ie-2","ie-2",42000,"idb-elec",30,"High",[],0,0,0,"none",false,"none",0],
  ["ip-109","APP-2009","Trim accessory bundle","620590","","ie-5","ie-5",18000,"idb-apparel",38,"High",[],0,0,0,"none",false,"low",0],
];

export const IMPORT_PRODUCTS: ImportProduct[] = ROWS.map((r) => ({
  id: r[0],
  sku: r[1],
  name: r[2],
  description: `${r[2]} — imported under HTS ${r[3]} from ${r[4]}.`,
  htsCode: r[3],
  countryOfOrigin: r[4],
  supplierId: r[5],
  manufacturerId: r[6],
  declaredValue: r[7],
  unitOfMeasure: "Each",
  importDatabaseId: r[8],
  complianceScore: r[9],
  riskLevel: r[10],
  pgaFlags: r[11],
  section301: r[12] > 0,
  section301Rate: r[12],
  section232: r[13] > 0,
  section232Rate: r[13],
  additionalDutyPct: r[14],
  fta: r[15],
  ftaEligible: r[16],
  uflpaRisk: r[17],
}));

export const IMPORT_NOTIFICATIONS: ImportNotification[] = [
  { id: "in-1", type: "uflpa-flag", title: "UFLPA review required", body: "Women's cotton denim jeans flagged for Xinjiang sourcing.", productId: "ip-102", importDatabaseId: "idb-apparel", createdAt: addDays(-1), read: false },
  { id: "in-2", type: "pga-hold", title: "FDA hold on entry", body: "Active pharmaceutical ingredient pending FDA review.", productId: "ip-301", importDatabaseId: "idb-chem", createdAt: addDays(-2), read: false },
  { id: "in-3", type: "tariff-change", title: "Section 301 list update", body: "List 4A revisions impact 6 products in Electronics — APAC.", importDatabaseId: "idb-elec", createdAt: addDays(-3), read: false },
  { id: "in-4", type: "detention", title: "CBP detention notice", body: "Polyester fleece jackets detained at Long Beach.", productId: "ip-105", importDatabaseId: "idb-apparel", createdAt: addDays(-4), read: true },
  { id: "in-5", type: "fta-expiry", title: "USMCA certification expiring", body: "12 SKUs require recertification within 30 days.", importDatabaseId: "idb-auto", createdAt: addDays(-6), read: true },
];

export const PGA_AGENCIES: PgaAgency[] = ["FDA", "EPA", "USDA", "FWS", "CPSC", "TSCA", "DOT"];

export const getImportEntity = (id?: string) =>
  id ? IMPORT_ENTITIES.find((e) => e.id === id) ?? null : null;

/* ============================== Entries ============================== */

const PORTS = [
  "Los Angeles/Long Beach, CA",
  "Newark, NJ",
  "Savannah, GA",
  "Seattle/Tacoma, WA",
  "Houston, TX",
  "Charleston, SC",
  "Chicago, IL",
  "Laredo, TX",
];
const STATUSES: EntryStatus[] = ["Cleared", "Cleared", "Cleared", "Pending", "Hold", "Detained"];

function seeded(i: number, max: number) {
  return Math.abs(Math.sin(i * 9301 + 49297)) * max;
}

const ENTRIES: ImportEntry[] = [];
let entrySeq = 1;
IMPORT_PRODUCTS.forEach((p, pIdx) => {
  // Demo product gets richer data
  const count = p.id === "ip-405" ? 4 : p.id === "ip-102" ? 3 : 1 + Math.floor(seeded(pIdx, 3));
  for (let k = 0; k < count; k++) {
    const i = pIdx * 7 + k * 31;
    const days = -7 - Math.floor(seeded(i, 120));
    const port = PORTS[Math.floor(seeded(i + 1, PORTS.length)) % PORTS.length];
    const status =
      p.uflpaRisk === "high" && k === 0 ? "Detained" :
      p.pgaFlags.length && k === 0 ? "Hold" :
      STATUSES[Math.floor(seeded(i + 2, STATUSES.length)) % STATUSES.length];
    const value = Math.round(p.declaredValue * (0.6 + (seeded(i + 3, 80) / 100)));
    ENTRIES.push({
      id: `ie-entry-${entrySeq}`,
      entryNumber: `E${String(2025_0000 + entrySeq).slice(0, 8)}-${String(entrySeq).padStart(4, "0")}`,
      productId: p.id,
      importDatabaseId: p.importDatabaseId,
      entryDate: addDays(days),
      portOfEntry: port,
      value,
      status,
      countryOfOrigin: p.countryOfOrigin,
      htsCode: k === count - 1 && count > 1 ? `${p.htsCode.slice(0, -2)}${String((Number(p.htsCode.slice(-2)) + 1) % 99).padStart(2, "0")}` : p.htsCode,
      documents: [
        { id: `doc-${entrySeq}-1`, name: `invoice-${entrySeq}.pdf`, type: "Commercial Invoice", uploadedAt: addDays(days), size: `${180 + (i % 420)} KB` },
        { id: `doc-${entrySeq}-2`, name: `packing-list-${entrySeq}.pdf`, type: "Packing List", uploadedAt: addDays(days), size: `${90 + (i % 170)} KB` },
        ...(k === 0 ? [{ id: `doc-${entrySeq}-3`, name: `cbp-7501-${entrySeq}.pdf`, type: "Entry Summary / 7501" as const, uploadedAt: addDays(days + 1), size: `${240 + (i % 300)} KB` }] : []),
      ],
    });
    entrySeq++;
  }
});

export const IMPORT_ENTRIES: ImportEntry[] = ENTRIES;

export const getEntriesForProduct = (productId: string) =>
  IMPORT_ENTRIES.filter((e) => e.productId === productId);
export const getEntriesForDatabase = (dbId: string) =>
  IMPORT_ENTRIES.filter((e) => e.importDatabaseId === dbId);

/* ============================== Mutable create helpers (mock persistence) ============================== */

export function addImportProducts(rows: ImportProduct[]) {
  for (const r of rows) IMPORT_PRODUCTS.push(r);
}
export function addImportEntry(entry: ImportEntry) {
  IMPORT_ENTRIES.push(entry);
}
export function newEntryId() {
  return `ie-entry-${IMPORT_ENTRIES.length + 1000}`;
}
export function newProductId() {
  return `ip-new-${IMPORT_PRODUCTS.length + 1}`;
}
